/**
 * Motion Utilities for Framer Motion
 *
 * Helper hooks and utilities for managing animations with accessibility
 * and performance in mind.
 */

import { useEffect, useState } from 'react';
import type { Variants, Transition } from 'framer-motion';

// ============================================================================
// REDUCED MOTION HOOK
// ============================================================================

/**
 * Hook to detect user's reduced motion preference
 *
 * @returns {boolean} True if user prefers reduced motion
 *
 * @example
 * const prefersReducedMotion = useReducedMotion();
 * const transition = prefersReducedMotion ? { duration: 0 } : { duration: 0.5 };
 */
export function useReducedMotion(): boolean {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    // Check if window is available (SSR safety)
    if (typeof window === 'undefined') return;

    // Check media query
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);

    // Listen for changes
    const handleChange = (event: MediaQueryListEvent) => {
      setPrefersReducedMotion(event.matches);
    };

    mediaQuery.addEventListener('change', handleChange);

    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, []);

  return prefersReducedMotion;
}

// ============================================================================
// ANIMATION TOGGLE UTILITIES
// ============================================================================

/**
 * Simplify variants for reduced motion users
 *
 * @param variants - Original motion variants
 * @param shouldReduce - Whether to reduce motion
 * @returns Simplified or original variants
 *
 * @example
 * const variants = getAccessibleVariants(fadeUp, prefersReducedMotion);
 */
export function getAccessibleVariants(
  variants: Variants,
  shouldReduce: boolean
): Variants {
  if (!shouldReduce) return variants;

  // Create simplified versions with instant transitions
  const simplified: Variants = {};

  for (const key in variants) {
    const state = variants[key];

    if (typeof state === 'object' && state !== null) {
      // Keep final position/opacity but remove motion
      simplified[key] = {
        ...state,
        transition: { duration: 0 },
        // Remove transform-based properties for reduced motion
        x: undefined,
        y: undefined,
        scale: undefined,
        rotate: undefined,
      };
    } else {
      simplified[key] = state;
    }
  }

  return simplified;
}

/**
 * Get transition config respecting reduced motion
 *
 * @param transition - Original transition config
 * @param shouldReduce - Whether to reduce motion
 * @returns Instant or original transition
 *
 * @example
 * const transition = getAccessibleTransition({ duration: 0.5 }, prefersReducedMotion);
 */
export function getAccessibleTransition(
  transition: Transition,
  shouldReduce: boolean
): Transition {
  if (!shouldReduce) return transition;

  return {
    ...transition,
    duration: 0,
    delay: 0,
  };
}

// ============================================================================
// INTERSECTION OBSERVER UTILITIES
// ============================================================================

/**
 * Custom hook for intersection observer
 * Used for scroll-triggered animations
 *
 * @param options - IntersectionObserver options
 * @returns [ref, isIntersecting, entry]
 *
 * @example
 * const [ref, isVisible] = useIntersectionObserver({ threshold: 0.2 });
 */
export function useIntersectionObserver(
  options: IntersectionObserverInit = {}
): [
  (node: Element | null) => void,
  boolean,
  IntersectionObserverEntry | undefined
] {
  const [entry, setEntry] = useState<IntersectionObserverEntry>();
  const [node, setNode] = useState<Element | null>(null);

  const observer = typeof window !== 'undefined'
    ? new IntersectionObserver(([entry]) => setEntry(entry), {
        threshold: 0.2,
        rootMargin: '0px 0px -100px 0px',
        ...options,
      })
    : null;

  useEffect(() => {
    if (!observer || !node) return;

    observer.observe(node);

    return () => {
      observer.disconnect();
    };
  }, [node, observer]);

  return [setNode, !!entry?.isIntersecting, entry];
}

// ============================================================================
// STAGGER INDEX UTILITIES
// ============================================================================

/**
 * Calculate stagger delay for list item
 *
 * @param index - Item index in list
 * @param baseDelay - Base delay between items (default 0.05)
 * @param maxDelay - Maximum total delay (default 1.5)
 * @returns Calculated delay in seconds
 *
 * @example
 * const delay = getStaggerDelay(5, 0.1); // 0.5s
 */
export function getStaggerDelay(
  index: number,
  baseDelay: number = 0.05,
  maxDelay: number = 1.5
): number {
  const calculatedDelay = index * baseDelay;
  return Math.min(calculatedDelay, maxDelay);
}

/**
 * Calculate grid stagger delay (row + column)
 *
 * @param index - Item index
 * @param columns - Number of columns
 * @param rowDelay - Delay between rows (default 0.1)
 * @param colDelay - Delay between columns (default 0.03)
 * @returns Calculated delay in seconds
 *
 * @example
 * const delay = getGridStaggerDelay(7, 3); // Row 2, Col 1
 */
export function getGridStaggerDelay(
  index: number,
  columns: number,
  rowDelay: number = 0.1,
  colDelay: number = 0.03
): number {
  const row = Math.floor(index / columns);
  const col = index % columns;
  return row * rowDelay + col * colDelay;
}

// ============================================================================
// ANIMATION STATE UTILITIES
// ============================================================================

/**
 * Hook to manage animation state based on reduced motion
 *
 * @returns {object} Animation controls and state
 *
 * @example
 * const { shouldAnimate, variants } = useAnimationState();
 */
export function useAnimationState() {
  const prefersReducedMotion = useReducedMotion();

  return {
    shouldAnimate: !prefersReducedMotion,
    prefersReducedMotion,
    getVariants: (variants: Variants) =>
      getAccessibleVariants(variants, prefersReducedMotion),
    getTransition: (transition: Transition) =>
      getAccessibleTransition(transition, prefersReducedMotion),
  };
}

// ============================================================================
// SCROLL PROGRESS UTILITIES
// ============================================================================

/**
 * Hook to track scroll progress of an element
 *
 * @returns [ref, scrollProgress] - Element ref and scroll progress (0-1)
 *
 * @example
 * const [ref, progress] = useScrollProgress();
 * // Use progress for parallax or progress indicators
 */
export function useScrollProgress(): [
  (node: HTMLElement | null) => void,
  number
] {
  const [element, setElement] = useState<HTMLElement | null>(null);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (!element || typeof window === 'undefined') return;

    const handleScroll = () => {
      const rect = element.getBoundingClientRect();
      const elementHeight = element.offsetHeight;
      const viewportHeight = window.innerHeight;

      // Calculate scroll progress (0 = top enters, 1 = bottom leaves)
      const scrolled = (viewportHeight - rect.top) / (viewportHeight + elementHeight);
      const clamped = Math.max(0, Math.min(1, scrolled));

      setProgress(clamped);
    };

    handleScroll(); // Initial calculation
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [element]);

  return [setElement, progress];
}

// ============================================================================
// VIEWPORT SIZE UTILITIES
// ============================================================================

/**
 * Hook to detect viewport size changes
 *
 * @returns {object} Viewport width, height, and breakpoint info
 *
 * @example
 * const { isMobile, isTablet, isDesktop } = useViewportSize();
 */
export function useViewportSize() {
  const [viewport, setViewport] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 0,
    height: typeof window !== 'undefined' ? window.innerHeight : 0,
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleResize = () => {
      setViewport({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener('resize', handleResize, { passive: true });

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return {
    width: viewport.width,
    height: viewport.height,
    isMobile: viewport.width < 768,
    isTablet: viewport.width >= 768 && viewport.width < 1024,
    isDesktop: viewport.width >= 1024,
  };
}

// ============================================================================
// PERFORMANCE UTILITIES
// ============================================================================

/**
 * Debounce animation triggers for performance
 *
 * @param callback - Function to debounce
 * @param delay - Delay in milliseconds
 * @returns Debounced function
 */
export function debounce<T extends (...args: unknown[]) => unknown>(
  callback: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timeoutId: NodeJS.Timeout;

  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => callback(...args), delay);
  };
}

/**
 * Check if device likely has GPU acceleration
 *
 * @returns {boolean} True if GPU acceleration is likely available
 */
export function hasGPUAcceleration(): boolean {
  if (typeof window === 'undefined') return false;

  // Check for WebGL support as proxy for GPU capabilities
  try {
    const canvas = document.createElement('canvas');
    const gl =
      canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    return !!gl;
  } catch (e) {
    return false;
  }
}

/**
 * Get optimal animation quality based on device capabilities
 *
 * @returns {'high' | 'medium' | 'low'} Animation quality level
 */
export function getAnimationQuality(): 'high' | 'medium' | 'low' {
  if (typeof window === 'undefined') return 'medium';

  // Check for reduced motion preference first
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    return 'low';
  }

  // Check device capabilities
  const hasGPU = hasGPUAcceleration();
  const { isMobile } = {
    isMobile: window.innerWidth < 768,
  };

  if (hasGPU && !isMobile) return 'high';
  if (hasGPU && isMobile) return 'medium';
  return 'low';
}

// ============================================================================
// GESTURE UTILITIES (for mobile swipe)
// ============================================================================

/**
 * Swipe detection configuration
 */
export interface SwipeConfig {
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  onSwipeUp?: () => void;
  onSwipeDown?: () => void;
  threshold?: number; // Minimum distance in pixels (default: 50)
}

/**
 * Get swipe handlers for Framer Motion drag
 *
 * @param config - Swipe configuration
 * @returns Drag end handler
 *
 * @example
 * <motion.div
 *   drag="x"
 *   onDragEnd={getSwipeHandler({ onSwipeLeft: nextSlide })}
 * />
 */
export function getSwipeHandler(config: SwipeConfig) {
  const threshold = config.threshold ?? 50;

  return (_event: MouseEvent | TouchEvent | PointerEvent, info: { offset: { x: number; y: number } }) => {
    const { offset } = info;

    // Determine direction
    if (Math.abs(offset.x) > Math.abs(offset.y)) {
      // Horizontal swipe
      if (offset.x > threshold && config.onSwipeRight) {
        config.onSwipeRight();
      } else if (offset.x < -threshold && config.onSwipeLeft) {
        config.onSwipeLeft();
      }
    } else {
      // Vertical swipe
      if (offset.y > threshold && config.onSwipeDown) {
        config.onSwipeDown();
      } else if (offset.y < -threshold && config.onSwipeUp) {
        config.onSwipeUp();
      }
    }
  };
}
